// AGENT-A5: outer dashboard grid layout.
//
// CSS grid: 240px sidebar column on the left; on the right, stacked rows for
// viewSelectors, filterBar, (optional kpiRow), table, and charts. 20px gutters
// between cards.
//
// Slot order matches the PBI screenshot: PERIOD GRANULARITY (viewSelectors)
// sits FIRST under the TopBar, then the FilterBar, then optional KPIs,
// then table + charts.
//
// Slot contract (downstream agents render into these props):
//   - sidebar       — A5 (CategorySidebar)
//   - viewSelectors — A7
//   - filterBar     — A6
//   - table         — A7
//   - charts        — A8
//
// Responsive: when viewport <1100px, the sidebar collapses into a slide-in
// drawer driven by useUiStore.sidebarOpen (the top bar's menu button toggles).

import type { CSSProperties, ReactNode } from "react";
import { useCallback, useRef } from "react";
import { useUiStore } from "../state/uiStore";
import "./DashboardLayout.css";

export interface DashboardLayoutProps {
  sidebar: ReactNode;
  filterBar: ReactNode;
  /** Optional standalone period-selectors slot. As of the 2026-05-27 redesign
   *  the period selectors are rendered inside the FilterBar (connected panel),
   *  so this slot is normally omitted. */
  viewSelectors?: ReactNode;
  bookmarkBar?: ReactNode;
  /** AGENT-DASH-KPI: optional KPI row rendered above the metrics table. */
  kpiRow?: ReactNode;
  table: ReactNode;
  charts: ReactNode;
}

export function DashboardLayout(props: DashboardLayoutProps) {
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const sidebarWidth = useUiStore((s) => s.sidebarWidth);
  const setSidebarWidth = useUiStore((s) => s.setSidebarWidth);

  // AGENT-DASH-RESIZE: drag the right edge of the sidebar to widen / narrow
  // it. We capture the pointer and update the store live; the width feeds the
  // grid column via the `--dash-sidebar-w` custom property below.
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  const onResizeMove = useCallback(
    (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setSidebarWidth(d.startW + (e.clientX - d.startX));
    },
    [setSidebarWidth],
  );
  const onResizeEnd = useCallback(() => {
    dragRef.current = null;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
    window.removeEventListener("pointermove", onResizeMove);
    window.removeEventListener("pointerup", onResizeEnd);
  }, [onResizeMove]);
  const onResizeStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startW: sidebarWidth };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", onResizeMove);
      window.addEventListener("pointerup", onResizeEnd);
    },
    [sidebarWidth, onResizeMove, onResizeEnd],
  );

  const layoutStyle = {
    ["--dash-sidebar-w"]: `${sidebarWidth}px`,
  } as CSSProperties;

  return (
    <div
      className={`dash-layout${sidebarCollapsed ? " dash-layout--sidebar-collapsed" : ""}`}
      style={layoutStyle}
    >
      {/* Sidebar column — sticky on the desktop, slide-in drawer <1100px. */}
      <aside
        className={`dash-layout-sidebar${sidebarOpen ? " is-open" : ""}`}
        aria-label="Dashboard sidebar"
      >
        {props.sidebar}
        {/* Desktop-only drag handle to resize the sidebar (hidden <1100px
            and when the column is collapsed — see DashboardLayout.css). */}
        <div
          className="dash-layout-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          onPointerDown={onResizeStart}
          onDoubleClick={() => setSidebarWidth(240)}
          title="Drag to resize · double-click to reset"
        />
      </aside>

      {/* Scrim that closes the drawer on outside click (mobile only). */}
      {sidebarOpen && (
        <button
          type="button"
          className="dash-layout-scrim"
          aria-label="Close sidebar"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="dash-layout-main">
        {/* Optional standalone period-selectors slot (normally rendered inside
            the FilterBar now — see FilterBar / ViewSelectors). */}
        {props.viewSelectors ? (
          <div className="dash-layout-slot dash-layout-slot--viewselectors">
            {props.viewSelectors}
          </div>
        ) : null}
        {props.bookmarkBar ? (
          <div className="dash-layout-slot dash-layout-slot--bookmark">
            {props.bookmarkBar}
          </div>
        ) : null}
        <div className="dash-layout-slot dash-layout-slot--filterbar">
          {props.filterBar}
        </div>
        {props.kpiRow ? (
          <div className="dash-layout-slot dash-layout-slot--kpis">
            {props.kpiRow}
          </div>
        ) : null}
        <div className="dash-layout-slot dash-layout-slot--charts">
          {props.charts}
        </div>
        <div className="dash-layout-slot dash-layout-slot--table">
          {props.table}
        </div>
      </div>
    </div>
  );
}
